'use client';

import { useState } from 'react';

import { CheckCircle, Minus, XCircle } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { FaqSection } from '@/components/sections/faq-section';
import { HelpdeskHero } from '@/components/sections/helpdesk-hero';
import { IndustryCustomers } from '@/components/sections/industry-customers';
import Logos from '@/components/sections/logos';
import Testimonials from '@/components/sections/testimonials';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

export default function TechnologyPage() {
  const { t } = useTranslation();
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  const comparisonData = [
    {
      metric: t.industry['Onboarding Time'],
      agent5: { value: t.industry['Instant guided setup'], status: 'best' },
      traditional: {
        value: t.industry['Wait for support call'],
        status: 'worst',
      },
    },
    {
      metric: t.industry['Technical Troubleshooting'],
      agent5: { value: t.industry['Step-by-step diagnostics'], status: 'best' },
      traditional: {
        value: t.industry['Generic help articles'],
        status: 'worst',
      },
    },
    {
      metric: t.industry['Feature Questions'],
      agent5: { value: t.industry['Contextual answers'], status: 'best' },
      traditional: {
        value: t.industry['Search documentation'],
        status: 'neutral',
      },
    },
    {
      metric: t.industry['API Support'],
      agent5: {
        value: t.industry['Code examples & debugging'],
        status: 'best',
      },
      traditional: {
        value: t.industry['Developer docs only'],
        status: 'worst',
      },
    },
    {
      metric: t.industry['Bug Reports'],
      agent5: { value: t.industry['Auto-logged with details'], status: 'best' },
      traditional: {
        value: t.industry['Manual form submission'],
        status: 'neutral',
      },
    },
    {
      metric: t.industry['Account Management'],
      agent5: {
        value: t.industry['Instant updates & changes'],
        status: 'best',
      },
      traditional: {
        value: t.industry['Email back-and-forth'],
        status: 'worst',
      },
    },
    {
      metric: t.industry['Churn Prevention'],
      agent5: {
        value: t.industry['Proactive help at friction'],
        status: 'best',
      },
      traditional: { value: t.industry['Users give up'], status: 'worst' },
    },
    {
      metric: t.industry['Cost per Ticket'],
      agent5: {
        value: t.industry['$0.75-$0.90 per resolution'],
        status: 'best',
      },
      traditional: { value: t.industry['$10-25 per ticket'], status: 'worst' },
    },
  ];

  return (
    <>
      <HelpdeskHero
        title={
          <>
            {t.industry['AI customer support for']}
            <br />
            {t.industry['for SaaS & tech platforms']}
          </>
        }
        subtitle={
          t.industry[
            'Your product is complex. Your users need help understanding features, fixing issues, and getting set up. Agent 5 handles onboarding, troubleshooting, and technical questions—so your team can focus on building product instead of answering the same questions.'
          ]
        }
        ctaText={t.industry['See how it works for SaaS']}
      />

      <Logos />

      <BenefitsSection
        title={t.industry['Support users through complexity']}
        subtitle={
          t.industry[
            'Help customers succeed with your product—from onboarding to power user.'
          ]
        }
        learnMoreLink="#faq"
        accentColor="emerald"
        backgroundColor="#000000"
        benefits={[
          {
            title: t.industry['Onboard new users faster'],
            description:
              t.industry[
                'New users ask: How do I set this up? Where do I start? What does this feature do? Agent 5 guides them through initial setup, explains key features, and gets them to their first success quickly.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.industry['Feature explanations and how-to questions'],
            description:
              t.industry[
                "Users ask how features work, what options mean, and how to achieve specific outcomes. Agent 5 provides contextual help based on what they're trying to accomplish—not just pointing to docs."
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: t.industry['Technical troubleshooting and bug reports'],
            description:
              t.industry[
                'Something not working? Error messages? Agent 5 diagnoses common issues, provides fixes, and automatically logs bugs with reproduction steps for your engineering team.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.industry['Integration and API support'],
            description:
              t.industry[
                'Developers ask about API endpoints, authentication, webhooks, and integration steps. Agent 5 provides code examples, troubleshoots connection issues, and explains technical concepts clearly.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: t.industry['Account and billing management'],
            description:
              t.industry[
                'Upgrade plan? Update payment method? Need an invoice? Agent 5 handles account changes, billing questions, and subscription management—reducing churn from billing friction.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.industry['Proactive help at friction points'],
            description:
              t.industry[
                'Agent 5 detects when users struggle (failed attempts, repeated actions, stuck workflows) and offers help before they ask. Catches users before they churn out of frustration.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: t.industry['Scale support without hiring'],
            description:
              t.industry[
                'Your user base doubles. Your product gets more complex. Agent 5 scales with you—handling the volume increase and learning new features as you ship them.'
              ],
            linkText: '',
            linkHref: '#',
            imagePosition: 'right',
            statCard: {
              value: '77%',
              label: t.industry['Automation Rate'],
              sublabel: t.industry['For technical inquiries'],
            },
          },
        ]}
      />

      {/* Comparison Section */}
      <section className="bg-white py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {t.industry['Why tech companies choose Agent 5']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.industry[
                  'Built for products where customer success requires deep product knowledge.'
                ]
              }
            </p>
          </div>
          <div className="relative overflow-hidden p-8">
            <div className="border-border/50 bg-background/50 relative overflow-hidden border backdrop-blur-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-foreground font-semibold">
                      {t.industry['What Matters for SaaS']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.industry['Agent 5']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.industry['Traditional Support']}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow
                      key={index}
                      className="border-border/50"
                      onMouseEnter={() => setHoveredModel(row.metric)}
                      onMouseLeave={() => setHoveredModel(null)}
                    >
                      <TableCell className="text-foreground font-medium">
                        {row.metric}
                      </TableCell>
                      <TableCell className="text-center">
                        <div
                          className={cn(
                            'mx-auto inline-flex items-center gap-2 rounded-full px-3 py-1 transition-all',
                            hoveredModel === row.metric
                              ? 'bg-green-100'
                              : 'bg-transparent',
                          )}
                        >
                          {row.agent5.status === 'best' && (
                            <CheckCircle className="size-4 text-green-600" />
                          )}
                          {row.agent5.status === 'neutral' && (
                            <Minus className="size-4 text-yellow-600" />
                          )}
                          <span className="text-foreground text-sm">
                            {row.agent5.value}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div
                          className={cn(
                            'mx-auto inline-flex items-center gap-2 rounded-full px-3 py-1 transition-all',
                            hoveredModel === row.metric
                              ? 'bg-red-100'
                              : 'bg-transparent',
                          )}
                        >
                          {row.traditional.status === 'worst' && (
                            <XCircle className="size-4 text-red-600" />
                          )}
                          {row.traditional.status === 'neutral' && (
                            <Minus className="size-4 text-yellow-600" />
                          )}
                          <span className="text-muted-foreground text-sm">
                            {row.traditional.value}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={t.industry['Questions from SaaS product teams']}
        subtitle={
          t.industry['How Agent 5 helps complex products scale support.']
        }
        items={[
          {
            question: t.industry["Can Agent 5 learn our product's complexity?"],
            answer:
              t.industry[
                "Yes. We train Agent 5 on your documentation, feature specs, and common workflows. As you ship new features, we update the knowledge base. The AI understands context—when a user asks about integrations, it knows which plan they're on and what's available to them."
              ],
          },
          {
            question:
              t.industry['How does it handle technical troubleshooting?'],
            answer:
              t.industry[
                "Agent 5 walks users through diagnostic steps, checks common causes, and suggests fixes based on the specific error or behavior. If it can't resolve the issue, it logs details (browser, plan, steps to reproduce) and escalates to your team with everything needed to fix it."
              ],
          },
          {
            question:
              t.industry[
                'What about API and integration questions from developers?'
              ],
            answer:
              t.industry[
                'Agent 5 provides code examples, explains authentication flows, troubleshoots API errors, and helps developers integrate with your platform. It understands technical concepts and can debug common integration issues on the spot.'
              ],
          },
          {
            question:
              t.industry['Can it detect when users are about to churn?'],
            answer:
              t.industry[
                'Yes. Agent 5 identifies friction patterns (repeated failed attempts, downgrade requests, cancellation page visits) and proactively offers help. Often a simple explanation or quick fix prevents cancellation—catching users before they give up.'
              ],
          },
          {
            question:
              t.industry['How does it stay updated when we ship new features?'],
            answer:
              t.industry[
                'You update the knowledge base (API, documentation feed, or manual updates) and Agent 5 learns the new features immediately. No retraining delay. We also provide tools to test responses before they go live to users.'
              ],
          },
          {
            question:
              t.industry[
                'What happens with really complex or unique questions?'
              ],
            answer:
              t.industry[
                'Agent 5 escalates to your team with full conversation context. Your team gets the question, what\'s been tried, and relevant account details. No "can you explain your issue again"—they pick up exactly where the AI left off.'
              ],
          },
          {
            question:
              t.industry[
                'How long does it take to implement for a SaaS product?'
              ],
            answer:
              t.industry[
                'Most SaaS companies are live within 2-3 weeks. That includes knowledge base setup, integration with your product (if needed), training on your workflows, and team handoff processes. Complex products might take 4 weeks.'
              ],
          },
          {
            question:
              t.industry['Can we review and improve AI responses over time?'],
            answer:
              t.industry[
                'Yes. You see every conversation, flag incorrect responses, and provide corrections. Agent 5 learns from your feedback and gets better at handling your specific product nuances. Full transparency and control.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
